import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../../services/api';
import PasswordInput from '../Common/PasswordInput';
import MessageModal from '../Common/MessageModal';
import LoginPreferencesBar from './LoginPreferencesBar';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: url('/images/background.jpg') center/cover no-repeat fixed;
  padding: 20px;
  position: relative;
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(0, 128, 255, 0.82), rgba(1, 5, 84, 0.82));
    z-index: 1;
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 540px;
  position: relative;
  z-index: 2;
  background: ${({ theme }) => theme.colors.primary};
  border-radius: 12px;
  padding: 28px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const Title = styled.h1`
  margin: 0 0 10px;
  color: ${({ theme }) => theme.colors.white};
  font-size: 1.6rem;
`;

const Subtitle = styled.p`
  margin: 0 0 18px;
  color: rgba(255, 255, 255, 0.92);
`;

const ErrorText = styled.p`
  margin: 10px 0 0;
  color: #dc2626;
  font-weight: 600;
`;

const Button = styled.button`
  width: 100%;
  border: 0;
  border-radius: 8px;
  padding: 12px;
  background: ${({ theme }) => theme.colors.buttonPrimary};
  color: ${({ theme }) => theme.colors.white};
  font-weight: 600;
  cursor: pointer;
  margin-top: 12px;
  opacity: ${(props) => (props.disabled ? 0.65 : 1)};
  &:hover {
    background: ${({ theme }) => theme.colors.buttonPrimaryDark};
  }
`;

const Back = styled(Link)`
  display: inline-block;
  margin-top: 14px;
  color: ${({ theme }) => theme.colors.white};
  text-decoration: none;
  font-weight: 600;
  &:hover {
    text-decoration: underline;
  }
`;

const ResetPassword = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => String(searchParams.get('token') || '').trim(), [searchParams]);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [inlineError, setInlineError] = useState('');
  const [modal, setModal] = useState({
    open: false,
    title: '',
    message: '',
    type: 'success'
  });

  const closeModal = () => setModal((prev) => ({ ...prev, open: false }));
  const openModal = (title, message, type = 'success') => setModal({ open: true, title, message, type });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setInlineError('');
    if (!token) {
      setInlineError('Lien invalide: token manquant.');
      return;
    }
    if (password.length < 8) {
      setInlineError(t('messages.passwordTooShort'));
      return;
    }
    if (password !== confirm) {
      setInlineError('La confirmation ne correspond pas au mot de passe.');
      return;
    }
    setLoading(true);
    try {
      await authAPI.resetPassword({ token, newPassword: password });
      openModal(t('modal.success'), t('auth.resetSuccess'), 'success');
      setPassword('');
      setConfirm('');
    } catch (error) {
      const msg = error?.response?.data?.error?.message || 'Impossible de réinitialiser le mot de passe.';
      openModal(t('modal.error'), msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Card>
        <LoginPreferencesBar />
        <Title>{t('auth.resetTitle')}</Title>
        <Subtitle>{t('auth.resetSubtitle')}</Subtitle>
        <form onSubmit={handleSubmit}>
          <PasswordInput
            theme="dark"
            placeholder={t('forms.newPassword')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={loading}
          />
          <PasswordInput
            theme="dark"
            placeholder={t('forms.confirmPassword')}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            disabled={loading}
          />
          {inlineError ? <ErrorText>{inlineError}</ErrorText> : null}
          <Button type="submit" disabled={loading}>
            {loading ? t('common.loading') : t('auth.resetSubmit')}
          </Button>
        </form>
        <Back to="/">{t('auth.backToLogin')}</Back>
      </Card>

      <MessageModal
        isOpen={modal.open}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        variant="auth"
      />
    </Container>
  );
};

export default ResetPassword;

